/**
 * LoadingSpinner: 非同期通信中のローディングインジケーター共通コンポーネント。
 * 全ウィジェット（PreBookingForm/MultiCvAppointmentList/SurveyPopup/CancelModal）で使用する。
 *
 * 2種類のスピナーを提供:
 * - ブロックスピナー: カード/パネル領域の中央に表示する大型スピナー（空き枠取得待ち等）
 * - インラインスピナー: ボタン内に埋め込む小型スピナー（送信中表示）
 */

import { createElement } from "../core/shadowDomUtil";
import { TEXT_SECONDARY, TEXT_PRIMARY } from "./designTokens";

/** ローディングスピナーのCSS（各UIスタイル文字列に連結して使用する）。 */
export const LOADING_SPINNER_STYLES = `
@keyframes spica-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.spica-loading-block {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 32px 16px; gap: 12px;
}
.spica-loading-spinner {
  width: 28px; height: 28px; border: 3px solid #e6e6e6;
  border-top-color: ${TEXT_PRIMARY}; border-radius: 50%;
  animation: spica-spin 0.8s linear infinite;
}
.spica-loading-text {
  font-size: 12px; color: ${TEXT_SECONDARY}; margin: 0;
}
.spica-loading-inline {
  display: inline-block; width: 14px; height: 14px; border: 2px solid rgba(255,255,255,.4);
  border-top-color: #fff; border-radius: 50%;
  animation: spica-spin 0.8s linear infinite;
  vertical-align: middle; margin-right: 6px;
}
`;

export interface LoadingSpinnerOptions {
  /** 「読み込み中...」等のテキスト。省略時はデフォルト文言。 */
  text?: string;
}

/**
 * ブロックスピナーを生成する（カード/パネル領域に配置する用途）。
 * 呼び出し元が返されたelementをcontainerにappendChildし、通信完了後にremove()する。
 */
export function renderLoadingSpinner(options?: LoadingSpinnerOptions): HTMLElement {
  const wrapper = createElement("div", { className: "spica-loading-block" });
  wrapper.appendChild(createElement("div", { className: "spica-loading-spinner" }));
  wrapper.appendChild(createElement("p", { className: "spica-loading-text", text: options?.text ?? "読み込み中..." }));
  return wrapper;
}

/**
 * インラインスピナーを生成する（ボタン内に埋め込む用途）。
 * ボタンのtextContentをクリアし、このスピナー＋テキストノードを追加する形で使用する。
 */
export function renderInlineSpinner(): HTMLElement {
  return createElement("span", { className: "spica-loading-inline" });
}

/**
 * ボタンをローディング状態にする（二重送信防止＋視覚フィードバック）。
 * @returns 元の状態に復元する関数。通信完了後に呼ぶ。
 */
export function setButtonLoading(button: HTMLButtonElement, loadingText?: string): () => void {
  const originalText = button.textContent ?? "";
  const originalDisabled = button.disabled;

  button.disabled = true;
  button.textContent = "";
  button.appendChild(renderInlineSpinner());
  button.appendChild(document.createTextNode(loadingText ?? "処理中..."));

  return () => {
    button.disabled = originalDisabled;
    button.textContent = originalText;
  };
}
